import InsightsTooltip from 'components/ReusableComponents/Charts/Inslghts/InsightsTooltip'
import DataIsReprocessingBar from 'components/ReusableComponents/DataIsReprocessingBar'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import AddGuestIcon from 'components/ReusableComponents/Icons/AddGuest'
import ProfileCompletedIcon from 'components/ReusableComponents/Icons/ProfileCompleted'
import ResearchCompleteIcon from 'components/ReusableComponents/Icons/ResearchComplete'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import SmartDatepicker, {
  smartDateTypes
} from 'components/ReusableComponents/SmartDatepicker'
import { usePerformanceReportsDates } from 'features/Analytics'
import AnalyticTabs from 'features/Analytics/AnalyticTab'
import Diagnose from 'features/Diagnose'
import { barColors } from 'features/Engagement/engagementConstants'
import FactNumbers from 'features/Engagement/FactNumbers'
import useAllHotelsToggle from 'hooks/useAllHotels'
import { PMSs } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import subscriptionStatsApi from 'services/api/subscriptionStats'
import { formatNumberAsThousands } from 'utils/priceHelpers'

import styles from './SubscriptionAnalytics.module.scss'

const { thisWeek, last7days, last14days, last30days } = smartDateTypes

const ChartBox = ({
  isLoading,
  error,
  data = [],
  title,
  dataTitle,
  color = barColors[0]
}) => {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{title}</h3>
      </div>
      {isLoading && <LoadingIndicator inline />}
      {!isLoading && error && (
        <div className={styles.errorText}>
          Something went wrong while getting the data
        </div>
      )}
      {!isLoading && !error && data.length === 0 && (
        <div className={styles.errorText}>There is no data for selected date range</div>
      )}
      {!isLoading && !error && data && (
        <div className={styles.graphHolder}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              width={500}
              height={300}
              data={data}
              margin={{
                top: 0,
                right: 0,
                left: 0,
                bottom: 15
              }}
            >
              <CartesianGrid stroke="#F2F2F2" vertical={false} />
              <XAxis
                dataKey="date"
                axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
                tick={{
                  fill: '#7D7D7D',
                  fontSize: 12,
                  transform: 'translate(0, 14)'
                }}
                tickSize={0}
                padding={{ top: 30, bottom: 20, right: 40 }}
              />
              <YAxis
                tickSize={0}
                axisLine={false}
                allowDecimals={false}
                yAxisId="left"
                tick={{
                  fill: '#7D7D7D',
                  fontSize: 12,
                  transform: 'translate(-10, 0)'
                }}
                padding={{ top: 40, bottom: 0 }}
              />
              <Tooltip
                content={<InsightsTooltip dataTitle={dataTitle} />}
                cursor={{
                  strokeWidth: 0,
                  fill: '#dedede',
                  opacity: 0.22
                }}
              />
              <Line
                dataKey="subscriptions"
                stroke={color}
                type="monotone"
                dot={false}
                strokeWidth={2}
                yAxisId="left"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

const SubscriptionAnalytics = ({ showTabs }) => {
  const { fromDate, setFromDate, toDate, setToDate } = usePerformanceReportsDates()

  const {
    showAllHotels,
    setShowAllHotels,
    showWholeGroup,
    setShowWholeGroup,
    parentChain,
    setParentChain,
    idsOfCurrentGroup,
    idsOfAllHotels
  } = useAllHotelsToggle()

  const [totalSubscriptions, setTotalSubscriptions] = useState()
  const [deltaSubscriptions, setDeltaSubscriptions] = useState()

  const [totalUpdatedProfiles, setTotalUpdatedProfiles] = useState()
  const [deltaUpdatedProfiles, setDeltaUpdatedProfiles] = useState()

  const [totalEnrichedProfiles, setTotalEnrichedProfiles] = useState()
  const [deltaEnrichedProfiles, setDeltaEnrichedProfiles] = useState()

  const [subscriptions, setSubscriptions] = useState()
  const [subscriptionsError, setSubscriptionsError] = useState()
  const [isSubscriptionsLoading, setIsSubscriptionsLoading] = useState(true)

  const pms = useSelector(state => state.hotelDetails.pms)

  const hasDisconnectedPMS = pms === PMSs.disconnected

  useEffect(() => {
    setIsSubscriptionsLoading(true)

    let kind = 'CurrentSubsidiary'

    if (showAllHotels) {
      kind = 'AllSubsidiaries'
    }

    if (showWholeGroup) {
      kind = 'WholeGroup'
    }

    const abortController = new AbortController()

    subscriptionStatsApi
      .stats(
        {
          fromDate: moment(fromDate).format('YYYY-MM-DD'),
          toDate: moment(toDate).format('YYYY-MM-DD'),
          parentChainCode: parentChain,
          kind
        },
        abortController
      )
      .then(res => {
        setSubscriptionsError()
        setTotalSubscriptions(res.guestAddedTotal)
        setDeltaSubscriptions(res.guestAdded)
        setTotalUpdatedProfiles(res.guestUpdatedTotal)
        setDeltaUpdatedProfiles(res.guestUpdated)
        setTotalEnrichedProfiles(res.completedTotal)
        setDeltaEnrichedProfiles(res.completed)
        setIsSubscriptionsLoading(false)
      })
      .catch(err => {
        if (!err.toString().includes('The user aborted a request.')) {
          setSubscriptionsError(err.toString())
          setIsSubscriptionsLoading(false)
        }
      })

    return () => abortController.abort()
  }, [
    fromDate,
    toDate,
    showAllHotels,
    showWholeGroup,
    idsOfCurrentGroup,
    idsOfAllHotels,
    parentChain
  ])

  useEffect(() => {
    setIsSubscriptionsLoading(true)

    let kind = 'CurrentSubsidiary'

    if (showAllHotels) {
      kind = 'AllSubsidiaries'
    }

    if (showWholeGroup) {
      kind = 'WholeGroup'
    }

    const abortController = new AbortController()

    subscriptionStatsApi
      .added(
        {
          fromDate: moment(fromDate).format('YYYY-MM-DD'),
          toDate: moment(toDate).format('YYYY-MM-DD'),
          parentChainCode: parentChain,
          kind
        },
        abortController
      )
      .then(res => {
        setSubscriptions(res.daily)
        setSubscriptionsError()
        setIsSubscriptionsLoading(false)
      })
      .catch(err => {
        if (!err.toString().includes('The user aborted a request.')) {
          setSubscriptions()
          setSubscriptionsError(err.toString())
          setIsSubscriptionsLoading(false)
        }
      })

    return () => abortController.abort()
  }, [
    fromDate,
    toDate,
    showAllHotels,
    showWholeGroup,
    idsOfCurrentGroup,
    idsOfAllHotels,
    parentChain
  ])

  const [profilesUpdated, setProfilesUpdated] = useState()
  const [profilesUpdatedError, setProfilesUpdatedError] = useState()
  const [isProfilesUpdatedLoading, setIsProfilesUpdatedLoading] = useState(true)

  useEffect(() => {
    setIsProfilesUpdatedLoading(true)

    let kind = 'CurrentSubsidiary'

    if (showAllHotels) {
      kind = 'AllSubsidiaries'
    }

    if (showWholeGroup) {
      kind = 'WholeGroup'
    }

    const abortController = new AbortController()

    subscriptionStatsApi
      .updated(
        {
          fromDate: moment(fromDate).format('YYYY-MM-DD'),
          toDate: moment(toDate).format('YYYY-MM-DD'),
          parentChainCode: parentChain,
          kind
        },
        abortController
      )
      .then(res => {
        setProfilesUpdated(res.daily)
        setProfilesUpdatedError()
        setIsProfilesUpdatedLoading(false)
      })
      .catch(err => {
        if (!err.toString().includes('The user aborted a request.')) {
          setProfilesUpdated()
          setProfilesUpdatedError(err.toString())
          setIsProfilesUpdatedLoading(false)
        }
      })
    return () => abortController.abort()
  }, [
    fromDate,
    toDate,
    showAllHotels,
    showWholeGroup,
    idsOfCurrentGroup,
    idsOfAllHotels,
    parentChain
  ])

  const [profilesEnhanced, setprofilesEnhanced] = useState()
  const [profilesEnhancedError, setProfilesEnhancedError] = useState()
  const [isProfilesEnhancedLoading, setIsProfilesEnhancedLoading] = useState(true)

  useEffect(() => {
    setIsProfilesEnhancedLoading(true)

    let kind = 'CurrentSubsidiary'

    if (showAllHotels) {
      kind = 'AllSubsidiaries'
    }

    if (showWholeGroup) {
      kind = 'WholeGroup'
    }

    const abortController = new AbortController()

    subscriptionStatsApi
      .enhanced(
        {
          fromDate: moment(fromDate).format('YYYY-MM-DD'),
          toDate: moment(toDate).format('YYYY-MM-DD'),
          parentChainCode: parentChain,
          kind
        },
        abortController
      )
      .then(res => {
        setprofilesEnhanced(res.daily)
        setProfilesEnhancedError()
        setIsProfilesEnhancedLoading(false)
      })
      .catch(err => {
        if (!err.toString().includes('The user aborted a request.')) {
          setProfilesUpdated()
          setProfilesUpdatedError(err.toString())
          setIsProfilesEnhancedLoading(false)
        }
      })
    return () => abortController.abort()
  }, [
    fromDate,
    toDate,
    showAllHotels,
    showWholeGroup,
    idsOfCurrentGroup,
    idsOfAllHotels,
    parentChain
  ])

  const isFactsLoading =
    isSubscriptionsLoading || isProfilesUpdatedLoading || isProfilesEnhancedLoading

  const [frontDeskPerformance, setFrontDeskPerformance] = useState()
  const [frontDeskperformanceError, setFrontDeskPerformanceError] = useState()
  const [isFrontDeskPerformanceLoading, setIsFrontDeskPerformanceLoading] = useState(true)

  useEffect(() => {
    setIsFrontDeskPerformanceLoading(true)

    let kind = 'CurrentSubsidiary'

    if (showAllHotels) {
      kind = 'AllSubsidiaries'
    }

    if (showWholeGroup) {
      kind = 'WholeGroup'
    }

    const abortController = new AbortController()

    subscriptionStatsApi
      .frontDeskPerformance(
        {
          fromDate: moment(fromDate).format('YYYY-MM-DD'),
          toDate: moment(toDate).format('YYYY-MM-DD'),
          parentChainCode: parentChain,
          kind
        },
        abortController
      )
      .then(res => {
        setFrontDeskPerformance(Object.values(res.frontDeskSubscriptions))
        setFrontDeskPerformanceError()
        setIsFrontDeskPerformanceLoading(false)
      })
      .catch(err => {
        if (!err.toString().includes('The user aborted a request.')) {
          setFrontDeskPerformance()
          setFrontDeskPerformanceError(err.toString())
          setIsFrontDeskPerformanceLoading(false)
        }
      })

    return () => abortController.abort()
  }, [
    fromDate,
    toDate,
    showAllHotels,
    showWholeGroup,
    idsOfCurrentGroup,
    idsOfAllHotels,
    parentChain
  ])

  return (
    <div className={styles.wrapper}>
      {showTabs ? (
        <div className={styles.tabs}>
          <AnalyticTabs
            showWholeGroup={showWholeGroup}
            setShowWholeGroup={setShowWholeGroup}
            setShowAllHotels={setShowAllHotels}
            showAllHotels={showAllHotels}
            setFromDate={setFromDate}
            setToDate={setToDate}
            fromDate={fromDate}
            toDate={toDate}
            parentChain={parentChain}
            setParentChain={setParentChain}
          />
          <DataIsReprocessingBar />
          <Diagnose />
        </div>
      ) : (
        <>
          {!showTabs && <CustomHeader title="Subscriptions" width={1180} />}
          <div className={styles.datepickerHolder}>
            <SmartDatepicker
              isRange={true}
              startDate={fromDate}
              endDate={toDate}
              minDate={moment().add(-4, 'week')}
              onChangeStartDate={setFromDate}
              onChangeEndDate={setToDate}
              smartDates={[thisWeek, last7days, last14days, last30days]}
              isLimited={true}
            />
          </div>
        </>
      )}

      <div className={styles.row}>
        <FactNumbers>
          {isFactsLoading && (
            <div style={{ width: '100%' }}>
              <LoadingIndicator inline />
            </div>
          )}
          {!isFactsLoading && (
            <>
              <FactNumbers.Item
                value={formatNumberAsThousands(totalSubscriptions)}
                delta={deltaSubscriptions}
                Icon={AddGuestIcon}
                width={55}
                height={55}
                label="Guests subscribed"
              />
              {hasDisconnectedPMS && (
                <FactNumbers.Item
                  value={formatNumberAsThousands(totalUpdatedProfiles)}
                  delta={deltaUpdatedProfiles}
                  Icon={ProfileCompletedIcon}
                  label="Profiles completed"
                  width={55}
                  height={55}
                />
              )}
              <FactNumbers.Item
                value={formatNumberAsThousands(totalEnrichedProfiles)}
                delta={deltaEnrichedProfiles}
                Icon={ResearchCompleteIcon}
                width={55}
                height={55}
                label="Guest data enriched by AI"
              />
            </>
          )}
        </FactNumbers>
      </div>
      <div className={styles.cards}>
        {hasDisconnectedPMS && (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>Front-desk performance</h3>
            </div>
            {isFrontDeskPerformanceLoading && <LoadingIndicator inline />}
            {frontDeskperformanceError && (
              <div className={styles.errorText}>
                Something went wrong while getting the data
              </div>
            )}
            {!isFrontDeskPerformanceLoading &&
              !frontDeskperformanceError &&
              frontDeskPerformance && (
                <div className={styles.tableHolder}>
                  <table className={styles.table}>
                    <tr>
                      <th></th>
                      <th>Added guests</th>
                      <th>Completed profiles</th>
                    </tr>
                    {frontDeskPerformance
                      .slice(0, 5)
                      .filter(f => f.fullName !== 'Automation Directful')
                      .map(f => {
                        return (
                          <tr key={f.userId}>
                            <td>
                              {/* {i === 0 && <CrownIcon className={styles.crown} />} */}
                              {f.fullName}
                            </td>
                            <td>{f.guestsAdded}</td>
                            <td>{f.guestsCompleted}</td>
                          </tr>
                        )
                      })}
                  </table>
                </div>
              )}
          </div>
        )}
        <ChartBox
          title="Guests subscribed"
          isLoading={isSubscriptionsLoading}
          error={subscriptionsError}
          data={subscriptions}
          dataTitle="subscriptions"
          color={barColors[0]}
        />
        {hasDisconnectedPMS && (
          <ChartBox
            title="Profiles completed"
            isLoading={isProfilesUpdatedLoading}
            error={profilesUpdatedError}
            data={profilesUpdated}
            dataTitle="updated profiles"
            color={barColors[0]}
          />
        )}
        <ChartBox
          title="Guest data enriched by AI"
          isLoading={isProfilesEnhancedLoading}
          error={profilesEnhancedError}
          data={profilesEnhanced}
          dataTitle="enhanced profiles"
          color={barColors[0]}
        />
      </div>
    </div>
  )
}

export default SubscriptionAnalytics
